import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ApiError,
  getAuthSession,
  getBootstrap,
  getCorrectionStatus,
  getHealthStatus,
  getTodaySales,
  getVisitStatus,
  retryCorrection,
  signInWithGoogle,
  signOutGoogle,
  submitCorrection,
  submitVisit
} from './api';
import {
  modelMatchesSelection,
  normalizeModelText,
  reconcileDraftItemsWithCatalog,
  searchModels,
  selectableSalesModels
} from './model-search';
import {
  buildDraftSubmissionPayload,
  claimDraftOwnership,
  clearAllDrafts,
  clearDraft,
  loadDraft,
  loadRememberedReporter,
  loadRememberedStore,
  rememberReporter,
  rememberStore,
  saveDraft,
  type DraftOwnership
} from './storage';
import type {
  BootstrapCatalog,
  AuthSession,
  DraftItem,
  GpsCapture,
  ModelOption,
  SalesDraft,
  SalesResult,
  SubmitPayload,
  TodaySaleUnit,
  VisitDeliveryState
} from './types';
import { ACTIVITY_SUPPORT_STORE_ID, reconcileActivityDraft } from './activity-support';

const DEFAULT_OPTIONS = {
  gender: ['男', '女'],
  age_group: ['20以下', '21~30', '31~40', '41~50', '51以上'],
  identity: ['學生', '上班族', '退休', '家管', '其他'],
  size: ['22以下', '23~24', '25~27', '28以上'],
  motivation: ['新購', '原螢幕壞了要換機', '多買一台自用', '其他'],
  purpose: ['家用，文書居多', '家用，上網居多', '家用，電玩居多', '家用，看影片居多', '公司用', '其他'],
  no_sale_reasons: ['只是逛過來看看', '外觀不滿意', '畫質不夠好', '價格過高', '其他'],
  backfill_reason: ['現場來客較多／忙碌', '忘記即時填寫', '裝置或網路問題', '其他'],
  competitor_brand: ['Asus', 'Acer', 'AOC', 'BenQ', 'Dell', 'LG', 'ViewSonic', 'PHILIPS', '其他']
} as const;

type DraftSaveState = 'LOADING' | 'EMPTY' | 'SAVING' | 'SAVED' | 'ERROR';
type SubmitState = 'IDLE' | 'LOCATING' | 'SUBMITTING' | 'VERIFYING';

interface Receipt {
  result: SalesResult;
  soldQuantity: number;
}

declare global {
  interface Window {
    __salesPageRelease?: string;
    google?: {
      accounts: {
        id: {
          initialize(options: { client_id: string; callback(response: { credential?: string }): void; auto_select?: boolean }): void;
          renderButton(element: HTMLElement, options: Record<string, unknown>): void;
          disableAutoSelect(): void;
        };
      };
    };
  }
}

function newId(): string {
  return crypto.randomUUID();
}

function localDate(now = new Date()): string {
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function localTime(now = new Date()): string {
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function newItem(): DraftItem {
  return { item_id: newId(), model_id: '', model_text: '', quantity: 1 };
}

function newDraft(catalogVersion: string, reporterId = ''): SalesDraft {
  return {
    storage_version: 1,
    catalog_version: catalogVersion,
    visit_id: newId(),
    business_date: localDate(),
    contact_time: localTime(),
    reporter_id: reporterId,
    temporary_reporter_name: '',
    store_id: '',
    activity_id: '',
    is_backfill: false,
    backfill_reason: '',
    result: 'SOLD',
    gender: '',
    age_group: '',
    identity: '',
    identity_other: '',
    size: '',
    motivation: '',
    motivation_other: '',
    purpose: '',
    purpose_other: '',
    no_sale_reasons: [],
    no_sale_reason_other: '',
    competitor_brand: '',
    competitor_brand_other: '',
    competitor_model: '',
    summary: '',
    items: [newItem()],
    gps: null,
    submission_payload: undefined,
    phase: 'EDITING',
    updated_at: new Date().toISOString()
  };
}

function hasMeaningfulDraft(draft: SalesDraft): boolean {
  return draft.phase !== 'EDITING'
    || Boolean(draft.gps)
    || draft.is_backfill
    || draft.business_date < localDate()
    || Boolean(draft.temporary_reporter_name.trim())
    || Boolean(draft.backfill_reason.trim())
    || draft.result !== 'SOLD'
    || Boolean(draft.gender || draft.age_group || draft.identity || draft.identity_other
      || draft.size || draft.motivation || draft.motivation_other || draft.purpose || draft.purpose_other
      || draft.no_sale_reasons.length || draft.no_sale_reason_other || draft.competitor_brand || draft.competitor_brand_other
      || draft.competitor_model || draft.summary.trim())
    || draft.items.length !== 1
    || draft.items.some(item => Boolean(item.model_id || item.model_text.trim()) || item.quantity !== 1);
}

function independentDraft(current: SalesDraft): SalesDraft {
  return {
    ...current,
    visit_id: newId(),
    items: current.items.map(item => ({ ...item, item_id: newId() })),
    gps: null,
    submission_payload: undefined,
    phase: 'EDITING',
    updated_at: new Date().toISOString()
  };
}

function optionList(
  catalog: BootstrapCatalog,
  keys: string[],
  fallback: readonly string[]
): string[] {
  for (const key of keys) {
    const values = catalog.options[key];
    if (Array.isArray(values) && values.length) return values;
  }
  return [...fallback];
}

function activeReporterOptions(catalog: BootstrapCatalog) {
  const rank = (reporterId: string) => reporterId === 'test' ? 2 : reporterId === 'temporary' ? 1 : 0;
  return catalog.reporters
    .filter(reporter => reporter.active)
    .sort((left, right) => rank(left.reporter_id) - rank(right.reporter_id));
}

function optionLast(values: string[], value: string): string[] {
  return [...values.filter(option => option !== value), value];
}

function manualHref(): string {
  return new URLSearchParams(window.location.search).get('test') === '1' ? '/manual?test=1' : '/manual';
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

function focusField(id: string): void {
  window.requestAnimationFrame(() => document.getElementById(id)?.focus());
}

function capturedPosition(): Promise<GpsCapture> {
  if (!('geolocation' in navigator)) {
    return Promise.reject(new Error('這支裝置不支援定位，無法送出。'));
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy_m: position.coords.accuracy,
        captured_at: new Date(position.timestamp || Date.now()).toISOString()
      }),
      error => {
        const message = error.code === error.PERMISSION_DENIED
          ? '請允許定位權限後再送出。'
          : error.code === error.TIMEOUT
            ? '定位逾時，請移到訊號較好的位置再試。'
            : '目前無法取得定位，請稍後再試。';
        reject(new Error(message));
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 }
    );
  });
}

function ModelAutocomplete({
  item,
  models,
  index,
  onChange,
  onSelect,
  onRefreshModels,
  idPrefix = 'model',
  testIdPrefix = 'model-input',
  label = '商品型號'
}: {
  item: DraftItem;
  models: ModelOption[];
  index: number;
  onChange: (value: string) => void;
  onSelect: (model: ModelOption) => void;
  onRefreshModels?: () => Promise<void>;
  idPrefix?: string;
  testIdPrefix?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [refreshState, setRefreshState] = useState<'IDLE' | 'REFRESHING' | 'DONE' | 'FAILED'>('IDLE');
  const refreshedQueryRef = useRef('');
  const deferredQuery = useDeferredValue(item.model_text);
  const matches = useMemo(() => searchModels(models, deferredQuery), [deferredQuery, models]);
  const selectedModel = useMemo(
    () => models.find(model => model.model_id === item.model_id),
    [item.model_id, models]
  );
  const normalizedQuery = normalizeModelText(item.model_text);
  const normalizedLength = normalizedQuery.length;
  const inputId = `${idPrefix}-${item.item_id}`;
  const listId = `${idPrefix}-options-${item.item_id}`;

  useEffect(() => setActiveIndex(0), [deferredQuery]);
  useEffect(() => {
    if (!open || normalizedQuery.length < 4 || !onRefreshModels) {
      if (normalizedQuery.length < 4) {
        refreshedQueryRef.current = '';
        setRefreshState('IDLE');
      }
      return;
    }
    if (refreshedQueryRef.current === normalizedQuery) return;
    refreshedQueryRef.current = normalizedQuery;
    let cancelled = false;
    setRefreshState('REFRESHING');
    void onRefreshModels()
      .then(() => { if (!cancelled) setRefreshState('DONE'); })
      .catch(() => { if (!cancelled) setRefreshState('FAILED'); });
    return () => { cancelled = true; };
  }, [normalizedQuery, onRefreshModels, open]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!open || !matches.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(current => (current + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(current => (current - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const model = matches[activeIndex];
      if (model) {
        onSelect(model);
        setOpen(false);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="model-field">
      <label htmlFor={inputId}>{label}</label>
      <div className={`autocomplete ${selectedModel ? 'is-selected' : ''}`}>
        <span className="search-icon" aria-hidden="true">⌕</span>
        <input
          id={inputId}
          data-testid={`${testIdPrefix}-${index}`}
          type="search"
          inputMode="search"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open && matches.length > 0}
          aria-activedescendant={open && matches[activeIndex] ? `${listId}-${activeIndex}` : undefined}
          placeholder="輸入至少 4 碼搜尋型號"
          value={item.model_text}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 100)}
          onKeyDown={handleKeyDown}
          onChange={event => {
            onChange(event.target.value);
            setOpen(true);
          }}
        />
        {selectedModel ? <span className="selected-check" aria-label="已選取">✓</span> : null}
        {open && normalizedLength >= 4 ? (
          <div className="model-options" id={listId} role="listbox">
            {matches.length ? matches.map((model, matchIndex) => (
              <button
                key={model.model_id}
                id={`${listId}-${matchIndex}`}
                type="button"
                role="option"
                aria-selected={matchIndex === activeIndex}
                className={matchIndex === activeIndex ? 'is-active' : ''}
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  onSelect(model);
                  setOpen(false);
                }}
              >
                <strong>{model.name}</strong>
                <small>{[model.series, model.size ? `${model.size} 吋` : ''].filter(Boolean).join(' · ')}</small>
              </button>
            )) : (
              <p className="no-model" data-testid="model-refresh-state">
                {refreshState === 'REFRESHING'
                  ? '正在更新最新型號…'
                  : refreshState === 'FAILED'
                    ? '型號更新失敗，請檢查網路後重新輸入。'
                    : refreshState === 'DONE'
                      ? '已更新最新型號，仍找不到請聯絡管理者。'
                      : '正在確認最新型號…'}
              </p>
            )}
          </div>
        ) : null}
      </div>
      {normalizedLength > 0 && normalizedLength < 4 ? (
        <p className="field-hint">再輸入 {4 - normalizedLength} 碼即可搜尋。</p>
      ) : null}
      {selectedModel ? <p className="field-success">已選取 {selectedModel.name}</p> : null}
    </div>
  );
}

function QuantityControl({
  value,
  index,
  onChange
}: {
  value: number;
  index: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="quantity-field">
      <label htmlFor={`quantity-${index}`}>數量</label>
      <div className="quantity-control">
        <button type="button" aria-label="減少數量" onClick={() => onChange(Math.max(1, value - 1))}>−</button>
        <input
          id={`quantity-${index}`}
          data-testid={`quantity-input-${index}`}
          type="number"
          min="1"
          max="999"
          step="1"
          inputMode="numeric"
          value={value}
          onChange={event => onChange(Number(event.target.value))}
        />
        <button type="button" aria-label="增加數量" onClick={() => onChange(Math.min(999, value + 1))}>＋</button>
      </div>
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  options,
  onChange,
  required = true,
  className = ''
}: {
  id: string;
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={`field ${className}`}>
      <label htmlFor={id}>{label}{required ? <span aria-hidden="true"> *</span> : null}</label>
      <select id={id} required={required} value={value} onChange={event => onChange(event.target.value)}>
        <option value="">請選擇</option>
        {options.map(option => <option key={option} value={option}>{option}</option>)}
      </select>
    </div>
  );
}

function displaySubmittedTime(value: string): string {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('zh-TW', {
    timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', hour12: false
  }).format(parsed);
}

function TodaySalesPanel({
  units,
  loading,
  loadError,
  models,
  onRefresh,
  onBusyChange,
  onRefreshModels
}: {
  units: TodaySaleUnit[];
  loading: boolean;
  loadError: string;
  models: ModelOption[];
  onRefresh: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  onRefreshModels: () => Promise<void>;
}) {
  const [editingUnitId, setEditingUnitId] = useState('');
  const [model, setModel] = useState<DraftItem>(() => ({ item_id: 'correction-model', model_id: '', model_text: '', quantity: 1 }));
  const [state, setState] = useState<'IDLE' | 'SAVING' | 'VERIFYING'>('IDLE');
  const [error, setError] = useState('');
  const pendingPayloadRef = useRef<Parameters<typeof submitCorrection>[1] | null>(null);
  const recoveryAttemptedRef = useRef('');
  const busy = state !== 'IDLE';
  const displayUnits = useMemo(() => [...units].sort((left, right) => (
    Date.parse(right.submitted_at) - Date.parse(left.submitted_at)
  )), [units]);

  useEffect(() => onBusyChange(busy || Boolean(editingUnitId)), [busy, editingUnitId, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  const closeEditor = useCallback(() => {
    if (busy) return;
    setEditingUnitId('');
    setModel({ item_id: 'correction-model', model_id: '', model_text: '', quantity: 1 });
    setError('');
    pendingPayloadRef.current = null;
  }, [busy]);

  const finishCorrection = useCallback(async () => {
    await onRefresh();
    setState('IDLE');
    setEditingUnitId('');
    setModel({ item_id: 'correction-model', model_id: '', model_text: '', quantity: 1 });
    setError('');
    pendingPayloadRef.current = null;
  }, [onRefresh]);

  const pollCorrection = useCallback(async (correctionId: string, timeoutMs = 90_000): Promise<void> => {
    const startedAt = Date.now();
    setState('VERIFYING');
    while (Date.now() - startedAt < timeoutMs) {
      const status = await getCorrectionStatus(correctionId);
      if (status.delivery_status === 'COMPLETE') {
        await finishCorrection();
        return;
      }
      if (status.delivery_status === 'FAILED') {
        throw new ApiError('修改尚未完成，請再按一次送出修改。', 503, 'CORRECTION_DELIVERY_FAILED');
      }
      await sleep(1_000);
    }
    throw new ApiError('修改已收件，背景仍在寫入，請稍後再按一次確認。', 202, 'CORRECTION_PENDING');
  }, [finishCorrection]);

  useEffect(() => {
    const unit = units.find(candidate => candidate.correction_id && candidate.correction_status !== 'COMPLETE');
    const correctionId = unit?.correction_id || '';
    if (!unit || !correctionId || unit.correction_status === 'COMPLETE'
      || recoveryAttemptedRef.current === correctionId || editingUnitId || busy) return;
    recoveryAttemptedRef.current = correctionId;
    setError('');
    setState('VERIFYING');
    void (async () => {
      try {
        if (unit.correction_status === 'FAILED') await retryCorrection(correctionId);
        await pollCorrection(correctionId);
      } catch {
        setState('IDLE');
        setError('修改尚未完成，請稍後重新整理；系統會接續處理。');
      }
    })();
  }, [busy, editingUnitId, pollCorrection, units]);

  async function saveCorrection(): Promise<void> {
    const unit = units.find(candidate => candidate.unit_id === editingUnitId);
    if (!unit?.can_correct || busy) return;
    if (!model.model_id) {
      setError('請從候選清單選取正確型號。');
      focusField('correction-model-correction-model');
      return;
    }
    if (model.model_id === unit.model_id) {
      setError('請選擇與原紀錄不同的型號。');
      return;
    }
    setError('');
    try {
      let payload = pendingPayloadRef.current;
      if (!payload) {
        payload = {
          correction_id: newId(),
          unit_id: unit.unit_id,
          model_id: model.model_id
        };
        pendingPayloadRef.current = payload;
      }
      setState('SAVING');
      const accepted = await submitCorrection(unit.unit_id, payload);
      if (accepted.delivery_status === 'COMPLETE') await finishCorrection();
      else await pollCorrection(payload.correction_id);
    } catch (failure) {
      const payload = pendingPayloadRef.current;
      if (payload) {
        try {
          const status = await getCorrectionStatus(payload.correction_id);
          if (status.delivery_status === 'COMPLETE') {
            await finishCorrection();
            return;
          }
        } catch (statusFailure) {
          // Network uncertainty keeps the exact ID and payload for idempotent retry.
          if (statusFailure instanceof ApiError && statusFailure.status === 404) pendingPayloadRef.current = null;
        }
      }
      setState('IDLE');
      setError(failure instanceof Error ? failure.message : '修改失敗，請稍後再試。');
    }
  }

  return (
    <section className="today-sales" data-testid="today-sales-panel" aria-labelledby="today-sales-title">
      <div className="today-sales-heading">
        <div>
          <p className="eyebrow">TODAY</p>
          <h2 id="today-sales-title">今日已登記成交</h2>
        </div>
        <strong>{units.length} 台</strong>
      </div>
      {loading ? <p className="today-sales-state">正在載入今日紀錄…</p> : null}
      {!loading && loadError ? (
        <div className="today-sales-state error" role="status">
          <span>{loadError}</span>
          <button type="button" onClick={() => void onRefresh()}>重新載入</button>
        </div>
      ) : null}
      {!loading && !loadError && !units.length ? (
        <p className="today-sales-state">今天尚無成交紀錄。</p>
      ) : null}
      {!loading && !loadError && units.length ? (
        <ol className="today-sales-list">
          {displayUnits.map((unit, index) => (
            <li key={unit.unit_id} className="today-sale-row" data-testid="today-sale-row">
              <span className="today-sale-order">{index + 1}</span>
              <div className="today-sale-model">
                <strong>{unit.model_name}</strong>
                <small>{displaySubmittedTime(unit.submitted_at)}</small>
              </div>
              {!unit.corrected && unit.correction_id ? (
                <span className="corrected-badge" data-testid="correction-pending-badge">更正處理中</span>
              ) : null}
              {unit.can_correct ? (
                <button
                  type="button"
                  className="latest-sale-correct-button"
                  data-testid="latest-sale-correct-button"
                  disabled={busy}
                  onClick={() => {
                    setEditingUnitId(unit.unit_id);
                    setModel({ item_id: 'correction-model', model_id: '', model_text: '', quantity: 1 });
                    setError('');
                    pendingPayloadRef.current = null;
                  }}
                >修改</button>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      {error && !editingUnitId ? <p className="correction-error" role="alert">{error}</p> : null}
      {editingUnitId ? (
        <div className="correction-editor" data-testid="correction-editor">
          <h3>修改這台型號</h3>
          <ModelAutocomplete
            item={model}
            models={models}
            index={0}
            idPrefix="correction-model"
            testIdPrefix="correction-model-input"
            label="正確型號"
            onChange={value => {
              setModel(current => ({ ...current, model_text: value, model_id: '' }));
              pendingPayloadRef.current = null;
              setError('');
            }}
            onSelect={selected => {
              setModel(current => ({ ...current, model_id: selected.model_id, model_text: selected.name }));
              pendingPayloadRef.current = null;
              setError('');
            }}
            onRefreshModels={onRefreshModels}
          />
          {error ? <p className="correction-error" data-testid="correction-error" role="alert">{error}</p> : null}
          <div className="correction-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={closeEditor}>取消</button>
            <button type="button" className="primary-button" data-testid="correction-save" disabled={busy} onClick={() => void saveCorrection()}>
              {state === 'SAVING' ? '正在儲存…' : state === 'VERIFYING' ? '正在完成修改…' : '儲存修改'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function AppLoading({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <main className="loading-shell">
      <div className="loading-card">
        <div className="brand-mark" aria-hidden="true">MNT</div>
        <h1>駐點銷售回報</h1>
        {error ? (
          <>
            <p className="error-text">{error}</p>
            <button type="button" className="primary-button" onClick={onRetry}>重新載入</button>
          </>
        ) : (
          <>
            <div className="loading-line" />
            <p>正在載入人員、門市與型號主檔…</p>
          </>
        )}
      </div>
    </main>
  );
}

function CompletionView({ receipt, onNext, error }: { receipt: Receipt; onNext: () => void; error: string }) {
  const sold = receipt.result === 'SOLD';
  return (
    <main className="completion-shell" data-testid="completion-view">
      <section className="completion-card">
        <div className="completion-icon" aria-hidden="true">✓</div>
        <p className="eyebrow">回報資料已確認寫入</p>
        <h1>{sold ? '成交回報完成' : '未成交回報完成'}</h1>
        {sold ? (
          <div className="completion-metrics one">
            <div><strong>{receipt.soldQuantity}</strong><span>本次成交台數</span></div>
          </div>
        ) : null}
        {error ? (
          <div className="submit-error" role="alert">
            <strong>尚未開始下一筆</strong><span>{error}</span>
          </div>
        ) : null}
        <button type="button" className="primary-button" onClick={onNext}>填寫下一筆</button>
        <a className="completion-help" href={manualHref()}>查看操作說明</a>
      </section>
    </main>
  );
}

function GoogleSignInGate({ session, onSignedIn }: {
  session: AuthSession;
  onSignedIn(next: AuthSession): void;
}) {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    const render = () => {
      if (cancelled || !buttonRef.current) return;
      const identity = window.google?.accounts.id;
      if (!identity) {
        timer = window.setTimeout(render, 100);
        return;
      }
      identity.initialize({
        client_id: session.clientId,
        auto_select: false,
        callback: response => {
          if (!response.credential) {
            setError('未取得 Google 帳號，請重新選擇。');
            return;
          }
          setBusy(true);
          setError('');
          void signInWithGoogle(response.credential)
            .then(onSignedIn)
            .catch(reason => setError(reason instanceof Error ? reason.message : 'Google 登入失敗，請再試一次。'))
            .finally(() => setBusy(false));
        }
      });
      buttonRef.current.replaceChildren();
      identity.renderButton(buttonRef.current, {
        type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
        shape: 'rectangular', width: Math.min(360, Math.max(260, buttonRef.current.clientWidth || 320)), locale: 'zh_TW'
      });
    };
    render();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session.clientId, onSignedIn]);

  return (
    <main className="identity-gate">
      <section className="identity-gate-card" aria-labelledby="identity-gate-title">
        <span className="identity-mark" aria-hidden="true">✓</span>
        <p className="eyebrow">MNT STORE SALES</p>
        <h1 id="identity-gate-title">駐點銷售回報</h1>
        <p>請選擇您自己的 Google 帳號。這支手機完成一次登入後，之後會直接進入回報表。</p>
        <div ref={buttonRef} className="google-signin-button" aria-busy={busy}></div>
        {busy ? <p className="identity-state" role="status">正在確認帳號…</p> : null}
        {error ? <p className="identity-error" role="alert">{error}</p> : null}
        <a className="completion-help" href={manualHref()}>查看操作說明</a>
      </section>
    </main>
  );
}

function BlockedIdentityGate({ session, onSwitch }: {
  session: AuthSession;
  onSwitch(): Promise<void>;
}) {
  return (
    <main className="identity-gate">
      <section className="identity-gate-card" aria-labelledby="identity-blocked-title">
        <span className="identity-mark blocked" aria-hidden="true">!</span>
        <p className="eyebrow">MNT STORE SALES</p>
        <h1 id="identity-blocked-title">此帳號目前無法回報</h1>
        <p>{session.blockedMessage}</p>
        <p className="identity-account">{session.email}</p>
        <button type="button" className="identity-switch-button" onClick={() => void onSwitch()}>
          改用其他 Google 帳號
        </button>
      </section>
    </main>
  );
}

export function App() {
  const controlledTestUi = new URLSearchParams(window.location.search).get('test') === '1';
  const draftNamespace = controlledTestUi ? 'test' : 'formal';
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [catalog, setCatalog] = useState<BootstrapCatalog | null>(null);
  const [draft, setDraft] = useState<SalesDraft | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [draftSaveState, setDraftSaveState] = useState<DraftSaveState>('LOADING');
  const [submitState, setSubmitState] = useState<SubmitState>('IDLE');
  const [submitError, setSubmitError] = useState('');
  const [modelCatalogNotice, setModelCatalogNotice] = useState('');
  const [online, setOnline] = useState(() => navigator.onLine);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [todaySales, setTodaySales] = useState<TodaySaleUnit[]>([]);
  const [todaySalesLoading, setTodaySalesLoading] = useState(false);
  const [todaySalesError, setTodaySalesError] = useState('');
  const [correctionBusy, setCorrectionBusy] = useState(false);
  const [availableRelease, setAvailableRelease] = useState('');
  const [reloadBusy, setReloadBusy] = useState(false);
  const activeSubmissionRef = useRef('');
  const readyRef = useRef(false);
  const autosaveTimerRef = useRef<number | null>(null);
  const draftWriteVersionRef = useRef(0);
  const draftWriteChainRef = useRef<Promise<void>>(Promise.resolve());
  const draftOwnershipRef = useRef<DraftOwnership | null>(null);
  const loadGenerationRef = useRef(0);
  const recoveryAttemptedRef = useRef('');
  const modelRefreshPromiseRef = useRef<Promise<void> | null>(null);
  const modelRefreshedAtRef = useRef(0);

  const refreshTodaySales = useCallback(async (): Promise<void> => {
    if (controlledTestUi || !authSession?.enabled || !authSession.authenticated || authSession.blockedMessage) {
      setTodaySales([]);
      setTodaySalesError('');
      setTodaySalesLoading(false);
      return;
    }
    setTodaySalesLoading(true);
    setTodaySalesError('');
    try {
      const response = await getTodaySales();
      setTodaySales(response.units);
    } catch (error) {
      setTodaySalesError(error instanceof Error ? error.message : '目前無法載入今日成交紀錄。');
    } finally {
      setTodaySalesLoading(false);
    }
  }, [authSession, controlledTestUi]);

  const handleCorrectionBusyChange = useCallback((value: boolean) => {
    setCorrectionBusy(value);
  }, []);

  const refreshModelCatalog = useCallback(async (): Promise<void> => {
    if (modelRefreshPromiseRef.current) return modelRefreshPromiseRef.current;
    // A page may stay open for an entire shift. Refresh at the first model
    // search, then at most once every five minutes so an incomplete catalog
    // snapshot repairs itself without repeatedly reading Google Sheets.
    if (Date.now() - modelRefreshedAtRef.current < 5 * 60_000) return;
    const operation = getBootstrap(true)
      .then(nextCatalog => {
        setCatalog(current => current
          ? { ...current, version: nextCatalog.version, models: nextCatalog.models, stores: nextCatalog.stores, support_activities: nextCatalog.support_activities }
          : nextCatalog);
        modelRefreshedAtRef.current = Date.now();
      })
      .finally(() => { modelRefreshPromiseRef.current = null; });
    modelRefreshPromiseRef.current = operation;
    return operation;
  }, []);

  const queueDraftWrite = useCallback((nextDraft: SalesDraft): Promise<void> => {
    const version = ++draftWriteVersionRef.current;
    const operation = draftWriteChainRef.current
      .catch(() => undefined)
      .then(async () => {
        if (version !== draftWriteVersionRef.current) return;
        await saveDraft(nextDraft, draftNamespace);
      });
    draftWriteChainRef.current = operation;
    return operation;
  }, [draftNamespace]);

  const resetStoredDrafts = useCallback(async (): Promise<void> => {
    draftWriteVersionRef.current += 1;
    if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = null;
    await draftWriteChainRef.current.catch(() => undefined);

    let lastError: unknown = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await clearAllDrafts(draftNamespace);
        if (!await loadDraft(draftNamespace)) {
          draftWriteChainRef.current = Promise.resolve();
          return;
        }
        lastError = new Error('草稿清除後仍可讀回');
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error('無法完整清除原草稿，請重新整理後再試。');
  }, [draftNamespace]);

  const adoptDraftOwnership = useCallback(async (visitId: string): Promise<void> => {
    const ownership = await claimDraftOwnership(visitId);
    if (!ownership) throw new Error('無法鎖定這筆草稿，請重新整理後再試。');
    const previous = draftOwnershipRef.current;
    draftOwnershipRef.current = ownership;
    await previous?.release();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const generation = ++loadGenerationRef.current;
    setLoadError('');
    setDraftSaveState('LOADING');
    void (async () => {
      const [nextCatalog, savedDraft, nextAuth] = await Promise.all([
        getBootstrap(), loadDraft(draftNamespace), getAuthSession()
      ]);
        const activeReporters = activeReporterOptions(nextCatalog);
        const remembered = loadRememberedReporter();
        const rememberedStore = loadRememberedStore();
        const rememberedValid = activeReporters.some(reporter => reporter.reporter_id === remembered) ? remembered : '';
        const rememberedStoreValid = nextCatalog.stores.some(store => store.active && store.store_id === rememberedStore)
          ? rememberedStore : '';
        const savedReporterValid = savedDraft
          && activeReporters.some(reporter => reporter.reporter_id === savedDraft.reporter_id);
        const savedStoreValid = savedDraft
          && nextCatalog.stores.some(store => store.active && store.store_id === savedDraft.store_id);
        const testReporter = controlledTestUi
          && activeReporters.some(reporter => reporter.reporter_id === 'test') ? 'test' : '';
        const authenticatedReporter = !nextAuth.blockedMessage && nextAuth.enabled && nextAuth.authenticated
          ? nextAuth.reporter?.reporter_id || 'temporary'
          : '';
        const savedSubmission = savedDraft && savedDraft.phase !== 'EDITING'
          && Boolean(savedDraft.gps || savedDraft.submission_payload);
        let nextDraft = savedDraft
          ? {
              ...savedDraft,
              reporter_id: savedSubmission ? savedDraft.reporter_id
                : testReporter || authenticatedReporter || (savedReporterValid ? savedDraft.reporter_id : rememberedValid),
              temporary_reporter_name: savedDraft.temporary_reporter_name || '',
              store_id: savedSubmission || savedStoreValid ? savedDraft.store_id : rememberedStoreValid,
              activity_id: savedDraft.activity_id || '',
              is_backfill: Boolean(savedDraft.is_backfill),
              backfill_reason: savedDraft.backfill_reason || '',
              no_sale_reason_other: savedDraft.no_sale_reason_other || ''
            }
          : { ...newDraft(nextCatalog.version, testReporter || authenticatedReporter || rememberedValid), store_id: rememberedStoreValid };
        const draftModelReconciliation = nextDraft.phase === 'EDITING'
          ? reconcileDraftItemsWithCatalog(nextDraft.items, nextCatalog.models)
          : { items: nextDraft.items, clearedLabels: [] };
        if (draftModelReconciliation.clearedLabels.length) {
          nextDraft = { ...nextDraft, items: draftModelReconciliation.items };
        }
        if (nextDraft.phase === 'EDITING' && !nextDraft.is_backfill
          && nextDraft.business_date >= localDate()) {
          nextDraft = { ...nextDraft, business_date: localDate(), contact_time: localTime() };
        }
        nextDraft = reconcileActivityDraft(nextDraft, nextCatalog, localDate());
        let ownership = await claimDraftOwnership(nextDraft.visit_id);
        // A normal page reload can briefly overlap the previous document while
        // its Web Lock is being released. Retry the same draft before treating
        // it as a genuinely concurrent tab and cloning it.
        for (let attempt = 0; !ownership && attempt < 5; attempt += 1) {
          await sleep(200);
          ownership = await claimDraftOwnership(nextDraft.visit_id);
        }
        if (!ownership) throw new Error('目前草稿正在另一個分頁使用，請關閉另一個分頁後重新整理。');
        if (cancelled || generation !== loadGenerationRef.current) {
          await ownership.release();
          return;
        }
        const previous = draftOwnershipRef.current;
        draftOwnershipRef.current = ownership;
        await previous?.release();
        setCatalog(nextCatalog);
        setAuthSession(nextAuth);
        setDraft(nextDraft);
        setModelCatalogNotice(draftModelReconciliation.clearedLabels.length
          ? '草稿中已停用或不存在的成交型號已清除，請重新選擇正確型號。'
          : '');
        setDraftSaveState(hasMeaningfulDraft(nextDraft)
          ? (savedDraft && !draftModelReconciliation.clearedLabels.length ? 'SAVED' : 'SAVING')
          : 'EMPTY');
        readyRef.current = true;
    })().catch(error => {
      if (!cancelled) setLoadError(error instanceof Error ? error.message : '載入失敗，請稍後再試。');
    });
    return () => { cancelled = true; };
  }, [controlledTestUi, draftNamespace, loadAttempt]);

  useEffect(() => {
    const heartbeat = window.setInterval(() => {
      void draftOwnershipRef.current?.renew();
    }, 5_000);
    return () => {
      window.clearInterval(heartbeat);
      const ownership = draftOwnershipRef.current;
      draftOwnershipRef.current = null;
      void ownership?.release();
    };
  }, []);

  useEffect(() => {
    const updateOnline = () => setOnline(navigator.onLine);
    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOnline);
    return () => {
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOnline);
    };
  }, []);

  useEffect(() => {
    void refreshTodaySales();
  }, [refreshTodaySales]);

  useEffect(() => {
    if (!catalog || !draft || draft.phase !== 'EDITING') return;
    const reconciliation = reconcileDraftItemsWithCatalog(draft.items, catalog.models);
    if (!reconciliation.clearedLabels.length) return;
    setDraft(current => {
      if (!current || current.visit_id !== draft.visit_id || current.phase !== 'EDITING') return current;
      const currentReconciliation = reconcileDraftItemsWithCatalog(current.items, catalog.models);
      return currentReconciliation.clearedLabels.length
        ? { ...current, items: currentReconciliation.items }
        : current;
    });
    setModelCatalogNotice('草稿中已停用或不存在的成交型號已清除，請重新選擇正確型號。');
  }, [catalog, draft]);

  useEffect(() => {
    const pageRelease = String(window.__salesPageRelease || '');
    if (!pageRelease || pageRelease === 'unpackaged') return;
    let cancelled = false;
    let interval = 0;
    let initial = 0;
    const check = async () => {
      try {
        const health = await getHealthStatus();
        if (!cancelled && health.ok && health.releaseCommit === health.buildProbe
          && health.releaseCommit && health.releaseCommit !== pageRelease) {
          setAvailableRelease(health.releaseCommit);
        }
      } catch {
        // A version check is advisory and must never interrupt reporting.
      }
    };
    const onFocus = () => { void check(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    initial = window.setTimeout(() => { void check(); }, 1_500);
    interval = window.setInterval(() => { void check(); }, 5 * 60_000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(initial);
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!catalog || !draft) return;
    const stored = sessionStorage.getItem('mnt-sales-refresh-scroll');
    if (!stored) return;
    sessionStorage.removeItem('mnt-sales-refresh-scroll');
    const top = Number(stored);
    if (Number.isFinite(top) && top >= 0) {
      window.requestAnimationFrame(() => window.scrollTo({ top, behavior: 'auto' }));
    }
  }, [catalog, draft]);

  useEffect(() => {
    if (!readyRef.current || !draft || receipt) return;
    if (!hasMeaningfulDraft(draft)) {
      setDraftSaveState('EMPTY');
      if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
      const version = ++draftWriteVersionRef.current;
      const operation = draftWriteChainRef.current
        .catch(() => undefined)
        .then(async () => {
          if (version !== draftWriteVersionRef.current) return;
          await clearAllDrafts(draftNamespace);
        });
      draftWriteChainRef.current = operation;
      void operation.catch(() => setDraftSaveState('ERROR'));
      return;
    }
    setDraftSaveState('EMPTY');
    if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      const persistedDraft = { ...draft, updated_at: new Date().toISOString() };
      queueDraftWrite(persistedDraft)
        .then(() => {
          setDraftSaveState('SAVED');
        })
        .catch(() => setDraftSaveState('ERROR'));
    }, 500);
    return () => {
      if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    };
  }, [draft, draftNamespace, queueDraftWrite, receipt]);

  const completeDelivery = useCallback(async (_state: VisitDeliveryState, completedDraft: SalesDraft) => {
    const soldQuantity = completedDraft.result === 'SOLD'
      ? completedDraft.items.reduce((total, item) => total + item.quantity, 0)
      : 0;
    await resetStoredDrafts();
    const ownership = draftOwnershipRef.current;
    draftOwnershipRef.current = null;
    await ownership?.release();
    setDraft({
      ...newDraft(completedDraft.catalog_version, completedDraft.reporter_id),
      store_id: completedDraft.store_id,
      activity_id: completedDraft.activity_id || ''
    });
    setDraftSaveState('EMPTY');
    setReceipt({ result: completedDraft.result, soldQuantity });
    setSubmitState('IDLE');
    activeSubmissionRef.current = '';
    void refreshTodaySales();
  }, [refreshTodaySales, resetStoredDrafts]);

  const reconcileCompletedDelivery = useCallback(async (
    pendingDraft: SalesDraft,
    timeoutMs = 15_000
  ): Promise<boolean> => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      let state: VisitDeliveryState | null = null;
      try {
        state = await getVisitStatus(pendingDraft.visit_id);
      } catch {
        // The request may have reached the durable backend even when the
        // response or a status lookup was interrupted. Retry read-only status
        // checks before presenting the same accepted visit as an unsent draft.
      }
      if (state?.delivery_status === 'COMPLETE') {
        await completeDelivery(state, pendingDraft);
        return true;
      }
      if (state?.delivery_status === 'FAILED') return false;
      await sleep(1_000);
    }
    return false;
  }, [completeDelivery]);

  const pollDelivery = useCallback(async (pendingDraft: SalesDraft, timeoutMs = 90_000) => {
    const startedAt = Date.now();
    activeSubmissionRef.current = pendingDraft.visit_id;
    setSubmitState('VERIFYING');
    while (activeSubmissionRef.current === pendingDraft.visit_id && Date.now() - startedAt < timeoutMs) {
      const state = await getVisitStatus(pendingDraft.visit_id);
      if (state.delivery_status === 'COMPLETE') {
        await completeDelivery(state, pendingDraft);
        return;
      }
      if (state.delivery_status === 'FAILED') {
        throw new ApiError('回報尚未完成，請再按一次確認。', 503, 'DELIVERY_FAILED');
      }
      await sleep(1_000);
    }
    throw new ApiError('資料已收件，背景仍在寫入。請保留此頁並再按一次確認。', 202, 'DELIVERY_PENDING');
  }, [completeDelivery]);

  useEffect(() => {
    if (!catalog || !draft || draft.phase !== 'PENDING' || activeSubmissionRef.current) return;
    pollDelivery(draft, 20_000).catch(error => {
      activeSubmissionRef.current = '';
      setDraft(current => current ? { ...current, phase: 'FAILED' } : current);
      setSubmitState('IDLE');
      setSubmitError(error instanceof Error ? error.message : '尚未確認資料表寫入。');
    });
  }, [catalog, draft, pollDelivery]);

  useEffect(() => {
    if (!catalog || !draft?.gps || draft.phase !== 'FAILED' || activeSubmissionRef.current
      || recoveryAttemptedRef.current === draft.visit_id) return;
    recoveryAttemptedRef.current = draft.visit_id;
    activeSubmissionRef.current = draft.visit_id;
    setSubmitState('VERIFYING');
    void reconcileCompletedDelivery(draft, 20_000)
      .then(completed => {
        if (completed) return;
        activeSubmissionRef.current = '';
        setSubmitState('IDLE');
      })
      .catch(() => {
        activeSubmissionRef.current = '';
        setSubmitState('IDLE');
        setSubmitError('資料已寫入，但手機草稿尚未清除；請重新整理後再試。');
      });
  }, [catalog, draft, reconcileCompletedDelivery]);

  const updateDraft = useCallback((updates: Partial<SalesDraft>) => {
    setDraft(current => current ? { ...current, ...updates, submission_payload: undefined, phase: 'EDITING' } : current);
    setSubmitError('');
  }, []);

  const handleSignedIn = useCallback((next: AuthSession) => {
    setAuthSession(next);
    const reporterId = next.reporter?.reporter_id || 'temporary';
    rememberReporter(reporterId);
    setDraft(current => current
      ? { ...current, reporter_id: current.phase === 'EDITING' ? reporterId : current.reporter_id }
      : current);
    setSubmitError('');
  }, []);

  const switchGoogleAccount = useCallback(async () => {
    try {
      await signOutGoogle();
      window.google?.accounts.id.disableAutoSelect();
      setAuthSession(current => current ? { ...current, authenticated: false, email: '', reporter: null } : current);
      setSubmitError('');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '目前無法切換帳號，請稍後再試。');
    }
  }, []);

  useEffect(() => {
    if (!catalog) return;
    setDraft(current => current ? reconcileActivityDraft(current, catalog, localDate()) : current);
  }, [catalog, draft?.store_id, draft?.activity_id, draft?.business_date, draft?.is_backfill, draft?.phase]);

  const options = useMemo(() => catalog ? ({
    gender: optionList(catalog, ['gender', 'genders'], DEFAULT_OPTIONS.gender),
    ageGroup: optionList(catalog, ['age_group', 'age_groups'], DEFAULT_OPTIONS.age_group),
    identity: optionList(catalog, ['identity', 'identities'], DEFAULT_OPTIONS.identity),
    size: optionList(catalog, ['size', 'sizes'], DEFAULT_OPTIONS.size),
    motivation: optionList(catalog, ['motivation', 'motivations'], DEFAULT_OPTIONS.motivation),
    purpose: optionList(catalog, ['purpose', 'purposes'], DEFAULT_OPTIONS.purpose),
    noSaleReasons: optionLast(
      optionList(catalog, ['no_sale_reasons', 'no_sale_reason'], DEFAULT_OPTIONS.no_sale_reasons),
      '其他'
    ),
    backfillReason: optionList(catalog, ['backfill_reason', 'backfill_reasons'], DEFAULT_OPTIONS.backfill_reason),
    competitorBrand: optionList(catalog, ['competitor_brand', 'competitor_brands'], DEFAULT_OPTIONS.competitor_brand)
  }) : null, [catalog]);

  function setResult(result: SalesResult): void {
    setDraft(current => {
      if (!current) return current;
      return {
        ...current,
        result,
        items: result === 'SOLD' ? (current.items.length ? current.items : [newItem()]) : [],
        submission_payload: undefined,
        phase: 'EDITING'
      };
    });
    setSubmitError('');
  }

  function updateItem(itemId: string, updates: Partial<DraftItem>): void {
    setDraft(current => current ? {
      ...current,
      submission_payload: undefined,
      phase: 'EDITING',
      items: current.items.map(item => item.item_id === itemId ? { ...item, ...updates } : item)
    } : current);
    setSubmitError('');
  }

  function removeItem(itemId: string): void {
    setDraft(current => {
      if (!current || current.items.length <= 1) return current;
      return { ...current, submission_payload: undefined, phase: 'EDITING', items: current.items.filter(item => item.item_id !== itemId) };
    });
  }

  function toggleNoSaleReason(reason: string): void {
    setDraft(current => {
      if (!current) return current;
      const selected = current.no_sale_reasons.includes(reason)
        ? current.no_sale_reasons.filter(value => value !== reason)
        : [...current.no_sale_reasons, reason];
      return {
        ...current,
        no_sale_reasons: selected,
        no_sale_reason_other: reason === '其他' && !selected.includes('其他') ? '' : current.no_sale_reason_other,
        submission_payload: undefined,
        phase: 'EDITING'
      };
    });
  }

  function validate(current: SalesDraft): { message: string; field: string } | null {
    if (!current.reporter_id) return { message: '請選擇回報人員。', field: 'reporter' };
    if (current.reporter_id === 'temporary' && !current.temporary_reporter_name.trim()) {
      return { message: '請填寫實際回報人員姓名。', field: 'temporary-reporter-name' };
    }
    if (!current.store_id) return { message: '請選擇銷售地點。', field: 'store' };
    if (!current.business_date) return { message: '請填寫來客日期。', field: 'business-date' };
    if (current.business_date > localDate()) return { message: '來客日期不可晚於今天。', field: 'business-date' };
    if (!current.contact_time) return { message: '請填寫客到時間。', field: 'contact-time' };
    const isBackfill = current.is_backfill || current.business_date < localDate();
    if (isBackfill && !current.backfill_reason.trim()) {
      return { message: '補填資料請填寫補填原因。', field: 'backfill-reason' };
    }
    if (!current.gender) return { message: '請選擇來客性別。', field: 'gender' };
    if (!current.age_group) return { message: '請選擇年齡層。', field: 'age-group' };
    if (!current.identity || (current.identity === '其他' && !current.identity_other.trim())) {
      return { message: '請填寫來客身分。', field: current.identity === '其他' ? 'identity-other' : 'identity' };
    }
    if (!current.size) return { message: '請選擇想買或購買吋數。', field: 'size' };
    if (!current.motivation || (current.motivation === '其他' && !current.motivation_other.trim())) {
      return { message: '請填寫購買動機。', field: current.motivation === '其他' ? 'motivation-other' : 'motivation' };
    }
    if (!current.purpose || (current.purpose === '其他' && !current.purpose_other.trim())) {
      return { message: '請填寫購買用途。', field: current.purpose === '其他' ? 'purpose-other' : 'purpose' };
    }
    if (current.result === 'NOT_SOLD' && current.no_sale_reasons.includes('其他')
      && !current.no_sale_reason_other.trim()) {
      return { message: '請填寫其他未成交原因。', field: 'no-sale-reason-other' };
    }
    if (current.result === 'SOLD') {
      for (let index = 0; index < current.items.length; index += 1) {
        const item = current.items[index]!;
        if (!item.model_id) return { message: `請從候選清單選取第 ${index + 1} 款型號。`, field: `model-${item.item_id}` };
        if (!Number.isInteger(item.quantity) || item.quantity <= 0 || item.quantity > 999) {
          return { message: `第 ${index + 1} 款的數量必須是大於零的整數。`, field: `quantity-${index}` };
        }
      }
    }
    return null;
  }

  function buildPayload(current: SalesDraft, gps: GpsCapture, submissionDate = localDate()): SubmitPayload {
    return buildDraftSubmissionPayload(current, gps, submissionDate);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!draft || !catalog || submitState !== 'IDLE') return;
    setSubmitError('');
    recoveryAttemptedRef.current = '';
    let attemptedDraft: SalesDraft | null = null;
    let editableDraft = draft;
    let confirmedReceiptExists = false;
    try {
      const previousGps = draft.gps || draft.submission_payload?.gps;
      if (previousGps && draft.phase !== 'EDITING') {
        attemptedDraft = draft;
        setSubmitState('VERIFYING');
        activeSubmissionRef.current = draft.visit_id;
        try {
          const existing = await getVisitStatus(draft.visit_id);
          confirmedReceiptExists = true;
          if (existing.delivery_status === 'COMPLETE') {
            await completeDelivery(existing, draft);
            return;
          }
          if (existing.delivery_status !== 'FAILED') {
            await pollDelivery(draft);
            return;
          }

          // A terminal Sheet failure is retried with the exact persisted payload.
          // Re-capturing GPS here would change the payload for the same visit_id.
          setSubmitState('SUBMITTING');
          const retried = await submitVisit(buildPayload(draft, previousGps));
          if (retried.delivery_status === 'COMPLETE') {
            await completeDelivery(retried, draft);
          } else {
            await pollDelivery(draft);
          }
          return;
        } catch (error) {
          if (confirmedReceiptExists || !(error instanceof ApiError) || error.status !== 404) throw error;
          activeSubmissionRef.current = '';
          attemptedDraft = null;
          editableDraft = reconcileActivityDraft({ ...draft, phase: 'EDITING', gps: null, submission_payload: undefined }, catalog, localDate());
          setDraft(editableDraft);
          await queueDraftWrite(editableDraft);
          // The previous request never reached the durable backend. This is a
          // new send attempt, so it must always continue through a fresh
          // maximumAge: 0 geolocation capture instead of reusing draft GPS.
        }
      }

      const problem = validate(editableDraft);
      if (problem) {
        setSubmitState('IDLE');
        setSubmitError(problem.message);
        focusField(problem.field);
        return;
      }
      setSubmitState('LOCATING');
      const gps = await capturedPosition();
      const submissionTime = new Date();
      const submissionDate = localDate(submissionTime);
      const isBackfill = editableDraft.is_backfill || editableDraft.business_date < submissionDate;
      const preparedDraft: SalesDraft = {
        ...editableDraft,
        business_date: isBackfill ? editableDraft.business_date : submissionDate,
        contact_time: isBackfill ? editableDraft.contact_time : localTime(submissionTime),
        is_backfill: isBackfill,
        backfill_reason: isBackfill ? editableDraft.backfill_reason.trim() : '',
        gps,
        submission_payload: undefined,
        phase: 'EDITING',
        updated_at: submissionTime.toISOString()
      };
      const payload = buildPayload(preparedDraft, gps, submissionDate);
      const pendingDraft: SalesDraft = { ...preparedDraft, phase: 'PENDING', submission_payload: payload };
      attemptedDraft = pendingDraft;
      // Mark this tab as the active sender before exposing the PENDING draft.
      // Otherwise the resume effect can poll before POST /api/visits has created
      // the durable record, producing a transient 404 and a false failure state.
      activeSubmissionRef.current = pendingDraft.visit_id;
      setDraft(pendingDraft);
      await queueDraftWrite(pendingDraft);
      setSubmitState('SUBMITTING');
      const submitted = await submitVisit(payload);
      if (submitted.delivery_status === 'COMPLETE') {
        await completeDelivery(submitted, pendingDraft);
      } else {
        await pollDelivery(pendingDraft);
      }
    } catch (error) {
      activeSubmissionRef.current = '';
      if (error instanceof ApiError && error.code === 'GOOGLE_LOGIN_REQUIRED') {
        setAuthSession(current => current ? { ...current, authenticated: false, email: '', reporter: null } : current);
        setSubmitError('Google 登入已失效；草稿已保留，請重新選擇帳號後再送出。');
        setSubmitState('IDLE');
        setDraft(current => current ? { ...current, phase: 'FAILED' } : current);
        return;
      }
      if (error instanceof ApiError && error.code === 'VISIT_ID_CONFLICT' && confirmedReceiptExists) {
        // A legacy draft may straddle midnight at first send. Do not turn an
        // uncertain replay into a second accepted visit with a fresh ID.
        setSubmitError('這筆回報已收件，但手機內容與收件內容無法核對；請保留此頁並聯絡管理人員確認原回報。');
        setSubmitState('IDLE');
        setDraft(current => current ? { ...current, phase: 'FAILED' } : current);
        return;
      }
      if (error instanceof ApiError && error.code === 'VISIT_ID_CONFLICT') {
        const conflictedVisitId = draft.visit_id;
        const regenerated = independentDraft(draft);
        await adoptDraftOwnership(regenerated.visit_id);
        setDraft(regenerated);
        await queueDraftWrite(regenerated);
        await clearDraft(conflictedVisitId, draftNamespace);
        setSubmitError('先前草稿與已收資料不一致；欄位已保留，請重新按送出取得定位。');
        setSubmitState('IDLE');
        return;
      }
      let finalError: unknown = error;
      if (attemptedDraft) {
        try {
          if (await reconcileCompletedDelivery(attemptedDraft)) return;
        } catch (recoveryError) {
          finalError = recoveryError;
        }
      }
      const message = finalError instanceof Error ? finalError.message : '送出失敗，請稍後再試。';
      setSubmitError(message);
      setSubmitState('IDLE');
      setDraft(current => current ? { ...current, phase: 'FAILED' } : current);
    }
  }

  async function startNextReport(): Promise<void> {
    if (!catalog) return;
    const reporterId = draft?.reporter_id || loadRememberedReporter();
    const rememberedStore = draft?.store_id || loadRememberedStore();
    const storeId = catalog.stores.some(store => store.active && store.store_id === rememberedStore)
      ? rememberedStore : '';
    const next = {
      ...newDraft(catalog.version, reporterId),
      store_id: storeId,
      temporary_reporter_name: draft?.temporary_reporter_name || ''
    };
    let nextOwnership: DraftOwnership | null = null;
    try {
      nextOwnership = await claimDraftOwnership(next.visit_id);
      if (!nextOwnership) throw new Error('無法建立新回報，請關閉其他分頁後再試。');
      await resetStoredDrafts();
      const previous = draftOwnershipRef.current;
      draftOwnershipRef.current = nextOwnership;
      nextOwnership = null;
      await previous?.release();
      setReceipt(null);
      setDraft(next);
      setSubmitError('');
      setModelCatalogNotice('');
      setDraftSaveState('EMPTY');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      await nextOwnership?.release();
      setSubmitError(error instanceof Error ? error.message : '無法建立新回報。');
    }
  }

  async function startFreshReport(): Promise<void> {
    if (!catalog || !draft || submitState !== 'IDLE') return;
    setSubmitError('');
    setModelCatalogNotice('');
    const next = {
      ...newDraft(catalog.version, draft.reporter_id || loadRememberedReporter()),
      store_id: draft.store_id || loadRememberedStore(),
      temporary_reporter_name: draft.temporary_reporter_name
    };
    let nextOwnership: DraftOwnership | null = null;
    try {
      nextOwnership = await claimDraftOwnership(next.visit_id);
      if (!nextOwnership) throw new Error('無法建立新回報，請關閉其他分頁後再試。');
      await resetStoredDrafts();
      const previous = draftOwnershipRef.current;
      draftOwnershipRef.current = nextOwnership;
      nextOwnership = null;
      await previous?.release();
      setDraft(next);
      setReceipt(null);
      setSubmitState('IDLE');
      setDraftSaveState('EMPTY');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      await nextOwnership?.release();
      setSubmitError(error instanceof Error ? error.message : '無法建立新回報。');
    }
  }

  async function reloadLatestRelease(): Promise<void> {
    if (!availableRelease || !draft || submitState !== 'IDLE' || correctionBusy || reloadBusy) return;
    setReloadBusy(true);
    try {
      if (hasMeaningfulDraft(draft)) {
        await queueDraftWrite({ ...draft, updated_at: new Date().toISOString() });
      }
      sessionStorage.setItem('mnt-sales-refresh-scroll', String(window.scrollY));
      const next = new URL(window.location.href);
      next.searchParams.set('release', availableRelease.slice(0, 12));
      window.location.replace(next.toString());
    } catch {
      setReloadBusy(false);
      setSubmitError('目前無法保存畫面內容，請稍後再重新整理。');
    }
  }

  if (!catalog || !draft || !options || !authSession) {
    return <AppLoading error={loadError} onRetry={() => setLoadAttempt(value => value + 1)} />;
  }
  if (!controlledTestUi && authSession.enabled && !authSession.authenticated) {
    return <GoogleSignInGate session={authSession} onSignedIn={handleSignedIn} />;
  }
  if (!controlledTestUi && authSession.enabled && authSession.blockedMessage) {
    return <BlockedIdentityGate session={authSession} onSwitch={switchGoogleAccount} />;
  }
  if (receipt) return <CompletionView receipt={receipt} onNext={startNextReport} error={submitError} />;

  const activeReporters = activeReporterOptions(catalog);
  const activeStores = catalog.stores.filter(store => store.active);
  const activeModels = selectableSalesModels(catalog.models);
  const busy = submitState !== 'IDLE' || correctionBusy || reloadBusy;
  const hasDraftContent = hasMeaningfulDraft(draft);
  const isBackfillMode = draft.is_backfill || draft.business_date < localDate();
  const totalQuantity = draft.result === 'SOLD'
    ? draft.items.reduce((total, item) => total + (Number.isInteger(item.quantity) ? item.quantity : 0), 0)
    : 0;
  const saveLabel = !online
    ? '目前離線，草稿保留中'
    : draftSaveState === 'SAVING'
      ? '草稿儲存中…'
      : draftSaveState === 'ERROR'
        ? '草稿保存失敗'
        : '草稿已保存';
  const submitLabel = submitState === 'LOCATING'
    ? '正在送出…'
    : submitState === 'SUBMITTING'
      ? '正在送出…'
      : submitState === 'VERIFYING'
        ? '正在完成回報…'
        : draft.phase === 'FAILED'
          ? draft.gps
            ? '確認上次送出結果'
            : '重新送出定位及資料'
          : '送出定位及資料';

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">MNT STORE SALES</p>
          <h1>駐點銷售回報</h1>
        </div>
        <div className="topbar-actions">
          {authSession.enabled && !controlledTestUi ? (
            <button type="button" className="account-button" disabled={busy || draft.phase !== 'EDITING'} onClick={() => void switchGoogleAccount()}>
              <span>{authSession.reporter?.name || '臨時'}</span>
              <small>切換帳號</small>
            </button>
          ) : null}
          <a className="manual-link" href={manualHref()}>使用說明</a>
          {hasDraftContent ? (
            <div className={`draft-state ${draftSaveState === 'ERROR' ? 'error' : ''}`} aria-live="polite">
              <span aria-hidden="true">{online ? '▣' : '↯'}</span>{saveLabel}
            </div>
          ) : null}
        </div>
      </header>

      {availableRelease ? (
        <section className="release-update" role="status" data-testid="release-update">
          <div><strong>表單已更新</strong><span>重新整理後即可使用最新版，已填內容會保留。</span></div>
          <button type="button" disabled={busy} onClick={() => void reloadLatestRelease()}>
            {reloadBusy ? '正在保存…' : '重新整理'}
          </button>
        </section>
      ) : null}

      {modelCatalogNotice ? (
        <section className="release-update" role="alert" data-testid="model-catalog-notice">
          <div><strong>型號已更新</strong><span>{modelCatalogNotice}</span></div>
        </section>
      ) : null}

      {!controlledTestUi && authSession.enabled && authSession.authenticated && !authSession.blockedMessage ? (
        <TodaySalesPanel
          units={todaySales}
          loading={todaySalesLoading}
          loadError={todaySalesError}
          models={activeModels}
          onRefresh={refreshTodaySales}
          onBusyChange={handleCorrectionBusyChange}
          onRefreshModels={refreshModelCatalog}
        />
      ) : null}

      {hasDraftContent ? (
        <section className="draft-toolbar" aria-label="未送草稿與新回報">
          <div className="draft-toolbar-copy">
            <strong>目前草稿</strong>
            <span>{draft.business_date} {draft.contact_time}</span>
          </div>
          <div className="draft-toolbar-actions">
            <button type="button" className="new-report-button" disabled={busy} onClick={() => void startFreshReport()}>
              ＋ 清除草稿，開始新回報
            </button>
          </div>
        </section>
      ) : null}

      <form className="report-form" noValidate onSubmit={handleSubmit}>
        <fieldset className="form-lock" disabled={busy} aria-busy={busy}>
        <section className="form-card basic-card" aria-labelledby="basic-title">
          <div className="section-heading">
            <span className="section-number">1</span>
            <div><h2 id="basic-title">本次來客</h2><p>人員與門市分開記錄</p></div>
          </div>
          <div className="field-grid basic-grid">
            <div className="field full-field">
              <label htmlFor="reporter">回報人員 <span aria-hidden="true">*</span></label>
              {controlledTestUi ? (
                <div id="reporter" className="verified-reporter" data-testid="verified-reporter" tabIndex={-1}>
                  <strong>受控測試</strong>
                  <span>test（資料與正式統計隔離）</span>
                </div>
              ) : authSession.enabled ? (
                <div id="reporter" className="verified-reporter" data-testid="verified-reporter" tabIndex={-1}>
                  <strong>{authSession.reporter?.name || '臨時'}</strong>
                  <span>{authSession.email}</span>
                </div>
              ) : (
                <select
                  id="reporter"
                  data-testid="reporter-select"
                  value={draft.reporter_id}
                  onChange={event => {
                    rememberReporter(event.target.value);
                    updateDraft({ reporter_id: event.target.value });
                  }}
                >
                  <option value="">請選擇回報人員</option>
                  {activeReporters.map(reporter => (
                    <option key={reporter.reporter_id} value={reporter.reporter_id}>{reporter.name}</option>
                  ))}
                </select>
              )}
              {draft.reporter_id === 'temporary' ? (
                <div className="temporary-name-field">
                  <label htmlFor="temporary-reporter-name">實際姓名 <span aria-hidden="true">*</span></label>
                  <input
                    id="temporary-reporter-name"
                    value={draft.temporary_reporter_name}
                    maxLength={40}
                    autoComplete="name"
                    onChange={event => updateDraft({ temporary_reporter_name: event.target.value })}
                  />
                </div>
              ) : null}
            </div>
            <div className="field full-field">
              <label htmlFor="store">銷售地點 <span aria-hidden="true">*</span></label>
              <select
                id="store"
                data-testid="store-select"
                value={draft.store_id}
                onChange={event => {
                  rememberStore(event.target.value);
                  updateDraft({ store_id: event.target.value });
                }}
              >
                <option value="">請選擇門市或活動支援</option>
                {activeStores.map(store => (
                  <option key={store.store_id} value={store.store_id}>{store.name}</option>
                ))}
              </select>
              <p className="field-hint">同一支手機下次會自動帶入。</p>
            </div>
            {draft.store_id === ACTIVITY_SUPPORT_STORE_ID ? (
              <div className="field full-field" data-testid="activity-support-section">
                <p className="field-hint">各自填報本人接待的來客與成交，依人員及日期統計。送出會記錄當下定位，不需事先設定活動名稱或地點。</p>
              </div>
            ) : null}
            <div className="field">
              <label htmlFor="business-date">來客日期 <span aria-hidden="true">*</span></label>
              <input
                id="business-date"
                type="date"
                max={localDate()}
                value={draft.business_date}
                onChange={event => updateDraft({ business_date: event.target.value })}
                disabled={!isBackfillMode}
              />
              {!isBackfillMode ? <p className="field-hint">一般回報於送出時自動記錄日期。</p> : null}
              {draft.business_date < localDate() ? <p className="backfill-tag">較早日期，已視為補填</p> : null}
            </div>
            <div className="field">
              <label htmlFor="contact-time">客到時間 <span aria-hidden="true">*</span></label>
              <input
                id="contact-time"
                type="time"
                value={draft.contact_time}
                onChange={event => updateDraft({ contact_time: event.target.value })}
                disabled={!isBackfillMode}
              />
              {!isBackfillMode ? <p className="field-hint">一般回報於送出時自動記錄當下時間。</p> : null}
            </div>
            <div className="backfill-control full-field">
              <label className="backfill-toggle" htmlFor="is-backfill">
                <input
                  id="is-backfill"
                  type="checkbox"
                  checked={isBackfillMode}
                  disabled={draft.business_date < localDate()}
                  onChange={event => updateDraft({
                    is_backfill: event.target.checked,
                    business_date: event.target.checked ? draft.business_date : localDate(),
                    contact_time: event.target.checked ? draft.contact_time : localTime(),
                    backfill_reason: event.target.checked ? draft.backfill_reason : ''
                  })}
                />
                <span><strong>這筆是補填</strong><small>勾選後才可修改來客日期與客到時間</small></span>
              </label>
              {isBackfillMode ? (
                <div className="field backfill-reason-field">
                  <label htmlFor="backfill-reason">補填原因 <span aria-hidden="true">*</span></label>
                  <input
                    id="backfill-reason"
                    list="backfill-reason-options"
                    maxLength={500}
                    placeholder="例如：現場來客較多，忙完後補填"
                    value={draft.backfill_reason}
                    onChange={event => updateDraft({ backfill_reason: event.target.value })}
                  />
                  <datalist id="backfill-reason-options">
                    {options.backfillReason.filter(reason => reason !== '其他')
                      .map(reason => <option key={reason} value={reason} />)}
                  </datalist>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="outcome-section" aria-labelledby="outcome-title">
          <h2 id="outcome-title" className="sr-only">成交結果</h2>
          <div className="segmented-control">
            <button
              type="button"
              data-testid="result-sold"
              aria-pressed={draft.result === 'SOLD'}
              className={draft.result === 'SOLD' ? 'is-selected' : ''}
              onClick={() => setResult('SOLD')}
            ><span aria-hidden="true">✓</span>成交</button>
            <button
              type="button"
              data-testid="result-not-sold"
              aria-pressed={draft.result === 'NOT_SOLD'}
              className={draft.result === 'NOT_SOLD' ? 'is-selected' : ''}
              onClick={() => setResult('NOT_SOLD')}
            ><span aria-hidden="true">○</span>未成交</button>
          </div>
        </section>

        {draft.result === 'SOLD' ? (
          <section className="form-card items-card" aria-labelledby="items-title" data-testid="items-section">
            <div className="section-heading with-summary">
              <span className="section-number">2</span>
              <div><h2 id="items-title">成交品項</h2><p>一筆來客可包含多款與數量</p></div>
              <span className="quantity-summary">共 {totalQuantity} 台</span>
            </div>
            <div className="item-list">
              {draft.items.map((item, index) => (
                <article className="item-editor" key={item.item_id} data-testid={`item-editor-${index}`}>
                  <div className="item-title">
                    <h3>品項 {index + 1}</h3>
                    {draft.items.length > 1 ? (
                      <button type="button" className="icon-button danger" aria-label={`刪除品項 ${index + 1}`} onClick={() => removeItem(item.item_id)}>刪除</button>
                    ) : null}
                  </div>
                  <ModelAutocomplete
                    item={item}
                    models={activeModels}
                    index={index}
                    onChange={value => {
                      const selected = activeModels.find(model => model.model_id === item.model_id);
                      updateItem(item.item_id, {
                        model_text: value,
                        model_id: selected && modelMatchesSelection(selected, value) ? selected.model_id : ''
                      });
                    }}
                    onSelect={model => {
                      updateItem(item.item_id, { model_id: model.model_id, model_text: model.name });
                      if (draft.items.every(entry => entry.item_id === item.item_id || Boolean(entry.model_id))) {
                        setModelCatalogNotice('');
                      }
                    }}
                    onRefreshModels={refreshModelCatalog}
                  />
                  <QuantityControl
                    value={item.quantity}
                    index={index}
                    onChange={quantity => updateItem(item.item_id, { quantity })}
                  />
                </article>
              ))}
            </div>
            <button
              type="button"
              className="add-item-button"
              data-testid="add-item"
              onClick={() => setDraft(current => current ? { ...current, items: [...current.items, newItem()], submission_payload: undefined, phase: 'EDITING' } : current)}
            ><span aria-hidden="true">＋</span>新增另一款</button>
          </section>
        ) : null}

        <section className="form-card customer-card" aria-labelledby="customer-title">
          <div className="section-heading">
            <span className="section-number">{draft.result === 'SOLD' ? '3' : '2'}</span>
            <div><h2 id="customer-title">來客資訊</h2><p>每位來客只填一次</p></div>
          </div>
          <div className="field-grid">
            <SelectField id="gender" label="來客性別" value={draft.gender} options={options.gender} onChange={value => updateDraft({ gender: value })} />
            <SelectField id="age-group" label="年齡層" value={draft.age_group} options={options.ageGroup} onChange={value => updateDraft({ age_group: value })} />
            <SelectField id="identity" label="來客身分" value={draft.identity} options={options.identity} onChange={value => updateDraft({ identity: value })} />
            {draft.identity === '其他' ? (
              <div className="field"><label htmlFor="identity-other">其他身分 *</label><input id="identity-other" value={draft.identity_other} onChange={event => updateDraft({ identity_other: event.target.value })} /></div>
            ) : null}
            <SelectField id="size" label="想買或購買吋數" value={draft.size} options={options.size} onChange={value => updateDraft({ size: value })} />
            <SelectField id="motivation" label="購買動機" value={draft.motivation} options={options.motivation} onChange={value => updateDraft({ motivation: value })} className="full-field" />
            {draft.motivation === '其他' ? (
              <div className="field full-field"><label htmlFor="motivation-other">其他購買動機 *</label><input id="motivation-other" value={draft.motivation_other} onChange={event => updateDraft({ motivation_other: event.target.value })} /></div>
            ) : null}
            <SelectField id="purpose" label="購買用途" value={draft.purpose} options={options.purpose} onChange={value => updateDraft({ purpose: value })} className="full-field" />
            {draft.purpose === '其他' ? (
              <div className="field full-field"><label htmlFor="purpose-other">其他購買用途 *</label><input id="purpose-other" value={draft.purpose_other} onChange={event => updateDraft({ purpose_other: event.target.value })} /></div>
            ) : null}
          </div>
        </section>

        {draft.result === 'NOT_SOLD' ? (
          <section className="form-card no-sale-card" aria-labelledby="no-sale-title">
            <div className="section-heading">
              <span className="section-number">3</span>
              <div><h2 id="no-sale-title">未成交紀錄</h2><p>原因可複選；競品資料依現況填寫</p></div>
            </div>
            <fieldset className="checkbox-fieldset">
              <legend>未成交原因</legend>
              <div className="checkbox-grid">
                {options.noSaleReasons.map(reason => (
                  <label key={reason} className={draft.no_sale_reasons.includes(reason) ? 'is-checked' : ''}>
                    <input type="checkbox" checked={draft.no_sale_reasons.includes(reason)} onChange={() => toggleNoSaleReason(reason)} />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="field-grid">
              {draft.no_sale_reasons.includes('其他') ? (
                <div className="field full-field">
                  <label htmlFor="no-sale-reason-other">其他未成交原因 *</label>
                  <input
                    id="no-sale-reason-other"
                    maxLength={500}
                    placeholder="請簡要說明其他未成交原因"
                    value={draft.no_sale_reason_other}
                    onChange={event => updateDraft({ no_sale_reason_other: event.target.value })}
                  />
                </div>
              ) : null}
              <SelectField id="competitor-brand" label="轉買品牌" value={draft.competitor_brand} options={options.competitorBrand} onChange={value => updateDraft({ competitor_brand: value })} required={false} />
              {draft.competitor_brand === '其他' ? (
                <div className="field"><label htmlFor="competitor-brand-other">其他品牌</label><input id="competitor-brand-other" value={draft.competitor_brand_other} onChange={event => updateDraft({ competitor_brand_other: event.target.value })} /></div>
              ) : (
                <div className="field"><label htmlFor="competitor-model">轉買型號</label><input id="competitor-model" value={draft.competitor_model} onChange={event => updateDraft({ competitor_model: event.target.value })} /></div>
              )}
              {draft.competitor_brand === '其他' ? (
                <div className="field full-field"><label htmlFor="competitor-model">轉買型號</label><input id="competitor-model" value={draft.competitor_model} onChange={event => updateDraft({ competitor_model: event.target.value })} /></div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="form-card notes-card" aria-labelledby="notes-title">
          <div className="section-heading compact">
            <span className="section-number">{draft.result === 'SOLD' ? '4' : '4'}</span>
            <div><h2 id="notes-title">對談摘要</h2><p>{draft.result === 'NOT_SOLD' ? '請簡述來客主要考量' : '成交可選填'}</p></div>
          </div>
          <div className="field full-field">
            <label className="sr-only" htmlFor="summary">我與來客的對談摘要</label>
            <textarea
              id="summary"
              rows={4}
              maxLength={2000}
              placeholder={draft.result === 'NOT_SOLD' ? '例如：比較價格、功能或仍需與家人討論…' : '需要補充時再填寫'}
              value={draft.summary}
              onChange={event => updateDraft({ summary: event.target.value })}
            />
            <span className="character-count">{draft.summary.length}/2000</span>
          </div>
        </section>

        {submitError ? (
          <div className="submit-error" role="alert" data-testid="submit-error">
            <strong>尚未完成</strong><span>{submitError}</span>
          </div>
        ) : null}

        {!correctionBusy ? (
          <div className="sticky-submit">
            <button type="submit" className="submit-button" data-testid="submit-button" disabled={busy || !online}>
              <span aria-hidden="true">⌖</span>{online ? submitLabel : '離線中，連線後可送出'}
            </button>
          </div>
        ) : null}
        </fieldset>
      </form>
    </div>
  );
}
